/**
 * Production-grade SSE streaming handler
 *
 * Simplified architecture:
 * - Backend sends: content_delta (text chunks) and done (completion)
 * - Frontend: Direct DOM updates during streaming
 * - React renders once when complete
 *
 * This matches ChatGPT/Claude production behavior.
 */

const log = () => {
}; // Debug disabled

/**
 * Process SSE streaming response with direct DOM updates
 * @param {Response} response - Fetch API response
 * @param {number} submissionId - Unique submission ID
 * @param {Function} setConversations - State setter
 * @param {Object} streamingHandlers - Handlers for appendText and complete
 * @param {Object} callbacks - Additional callbacks
 */
export async function processStreamingResponse(response, submissionId, setConversations, streamingHandlers, callbacks = {}) {
    const { setSessionId, setIsExecuting, refs = {} } = callbacks;
    const { isExecutingRef, inputRef } = refs;
    const { appendText, complete } = streamingHandlers;

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    log(`📡 Starting stream for submission ${submissionId}`);

    const startTime = Date.now();

    try {
        let buffer = '';
        let lastEventWasContentDelta = false;
        let hasContent = false;

        while (true) {
            const { done, value } = await reader.read();

            if (done) {
                log(`✓ Stream complete for submission ${submissionId}`);
                break;
            }

            // Decode chunk and add to buffer
            buffer += decoder.decode(value, { stream: true });

            // Process complete SSE messages
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer

            for (const line of lines) {
                if (!line.trim()) continue;

                if (line.startsWith('data: ')) {
                    const data = line.slice(6).trim();

                    if (data === '[DONE]') {
                        log('✓ Received [DONE] marker');
                        continue;
                    }

                    try {
                        const event = JSON.parse(data);

                        switch (event.type) {
                            case 'content_delta':
                                // Direct DOM update - no React re-render
                                if (event.delta) {
                                    // Insert line break when resuming text after a non-content event
                                    if (!lastEventWasContentDelta && hasContent) {
                                        appendText('\n\n');
                                        setConversations(prev => {
                                            const newConv = [...prev];
                                            const agentMsg = newConv[newConv.length - 1];
                                            if (agentMsg && agentMsg.type === 'agent') {
                                                agentMsg.finalResponse = (agentMsg.finalResponse || '') + '\n\n';
                                            }
                                            return newConv;
                                        });
                                    }
                                    hasContent = true;
                                    lastEventWasContentDelta = true;
                                    appendText(event.delta);

                                    // Update finalResponse in state (for history)
                                    setConversations(prev => {
                                        const newConv = [...prev];
                                        const agentMsg = newConv[newConv.length - 1];
                                        if (agentMsg && agentMsg.type === 'agent') {
                                            agentMsg.finalResponse = (agentMsg.finalResponse || '') + event.delta;
                                        }
                                        return newConv;
                                    });
                                }
                                break;

                            case 'todo_update':
                                // ✅ TODO TRACKING: Update todos in conversation state
                                lastEventWasContentDelta = false;
                                log(`📝 TODO update received: ${event.todos ? event.todos.length + ' todos' : 'status update'}`);

                                setConversations(prev => {
                                    const newConv = [...prev];
                                    const agentMsg = newConv[newConv.length - 1];
                                    if (agentMsg && agentMsg.type === 'agent') {
                                        if (event.todos) {
                                            // Full todo list (initial)
                                            agentMsg.todos = event.todos;
                                        } else if (event.todo_id !== undefined) {
                                            // Single todo status/activity update
                                            const todo = agentMsg.todos?.find(t => t.id === event.todo_id);
                                            if (todo) {
                                                if (event.status) {
                                                    todo.status = event.status;
                                                }
                                                // ✅ Update inline activity
                                                if (event.activity !== undefined) {
                                                    todo.activity = event.activity;
                                                }
                                            }
                                        }
                                    }
                                    return newConv;
                                });
                                break;

                            case 'chart_data':
                                // Inline chart emitted by RenderChart tool — store on the message
                                lastEventWasContentDelta = false;
                                setConversations(prev => {
                                    const newConv = [...prev];
                                    const agentMsg = newConv[newConv.length - 1];
                                    if (agentMsg && agentMsg.type === 'agent') {
                                        agentMsg.chartData = event.chart;
                                    }
                                    return newConv;
                                });
                                break;

                            case 'topology_data':
                                // Inline network topology diagram emitted by RenderTopology tool
                                lastEventWasContentDelta = false;
                                setConversations(prev => {
                                    const newConv = [...prev];
                                    const agentMsg = newConv[newConv.length - 1];
                                    if (agentMsg && agentMsg.type === 'agent') {
                                        agentMsg.topologyData = event.topology;
                                    }
                                    return newConv;
                                });
                                break;

                            case 'done':
                                const queryTime = (Date.now() - startTime) / 1000;
                                log(`✓ Query complete in ${queryTime.toFixed(2)}s`);

                                // Trigger complete rendering
                                complete({ queryTime });

                                // Update conversation state
                                setConversations(prev => {
                                    const newConv = [...prev];
                                    const agentMsg = newConv[newConv.length - 1];
                                    if (agentMsg && agentMsg.type === 'agent') {
                                        agentMsg.isStreaming = false;
                                        agentMsg.processingStatus = 'done';
                                        agentMsg.queryTime = queryTime;

                                        // ✅ Auto-complete any incomplete TODOs when stream ends
                                        if (agentMsg.todos?.length > 0) {
                                            agentMsg.todos = agentMsg.todos.map(todo => ({
                                                ...todo,
                                                status: todo.status === 'failed' ? 'failed' : 'completed',
                                                activity: null  // Clear any "Calling X..." messages
                                            }));
                                        }
                                    }
                                    return newConv;
                                });

                                // Update session if provided
                                if (event.session_id && setSessionId) {
                                    setSessionId(event.session_id);
                                    localStorage.setItem('session_id', event.session_id);
                                }
                                break;

                            case 'error':
                                setConversations(prev => {
                                    const newConv = [...prev];
                                    const agentMsg = newConv[newConv.length - 1];
                                    if (agentMsg && agentMsg.type === 'agent') {
                                        agentMsg.isStreaming = false;
                                        agentMsg.finalResponse = (agentMsg.finalResponse || '') + `\n\n❌ **Error:** ${event.message || 'An unexpected error occurred.'}`;
                                    }
                                    return newConv;
                                });
                                complete({ error: true });
                                break;

                            default:
                                log(`Unknown event type: ${event.type}`);
                        }
                    } catch (e) {
                        console.error('Failed to parse SSE event:', e, data);
                    }
                }
            }
        }
    } catch (error) {
        console.error('Stream error:', error);

        // Update conversation with error
        setConversations(prev => {
            const newConv = [...prev];
            const agentMsg = newConv[newConv.length - 1];
            if (agentMsg && agentMsg.type === 'agent') {
                agentMsg.isStreaming = false;
                agentMsg.finalResponse = agentMsg.finalResponse || '';
                agentMsg.finalResponse += `\n\n❌ **Stream Error:** ${error.message}`;
            }
            return newConv;
        });

        complete({ error: true });
    } finally {
        // Reset execution state
        if (setIsExecuting) setIsExecuting(false);
        if (isExecutingRef) isExecutingRef.current = false;
        if (inputRef?.current) inputRef.current.focus();
    }
}
